import {
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  effect,
  inject
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { Template } from '../../../../core/template';
import { emulateClick } from '../core/a11y';
import { Error } from '../core/error';
import { NotificationService } from '../notification/notification.service';
import { TelInputComponent } from '../shared/components/tel-input/tel-input.component';
import { AutoCloseDirective } from '../shared/directives/auto-close.directive';
import { HoverGradientDirective } from '../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../shared/directives/progress-click.directive';
import { Center } from '../shared/model/center.model';
import { AuthService } from '../shared/services/auth.service';
import { CenterService } from '../shared/services/center.service';
import { ContactService } from '../shared/services/contact.service';
import { SelectionService } from '../shared/services/selection.service';
import { SiteService } from '../shared/services/site.service';
import { EmailValidator } from '../shared/validators/validators';

type ContactForm = {
  center: FormControl<Center | null>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
  target: FormControl<'bath' | 'spa'>
  message: FormControl<string>;
  acceptTerms: FormControl<boolean>;
};

@Component({
  selector: 'app-contact',
  imports: [
    NgOptimizedImage,
    NgxTolgeeModule,
    NgClass,
    HoverGradientDirective,
    AutoCloseDirective,
    ReactiveFormsModule,
    ProgressClickDirective,
    TelInputComponent
  ],
  templateUrl: './contact.component.html'
})
export class ContactComponent {
  readonly emulateClick = emulateClick;
  authService = inject(AuthService);
  centerService = inject(CenterService);
  selectionService = inject(SelectionService);
  siteService = inject(SiteService);
  notificationService = inject(NotificationService);
  contactService = inject(ContactService);

  form: FormGroup<ContactForm>;

  constructor() {
    const user = this.authService.user();
    this.form = new FormGroup({
      center: new FormControl(this.selectionService.center(), {
        nonNullable: true,
        validators: [ Validators.required ]
      }),
      firstName: new FormControl(user?.firstName ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      lastName: new FormControl(user?.firstName ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      email: new FormControl(user?.email ?? '', {
        nonNullable: true,
        validators: [ Validators.required, EmailValidator ]
      }),
      phone: new FormControl(user?.phone ?? '', {
        nonNullable: true, validators: [ Validators.required, Validators.minLength(12) ]
      }),
      target: new FormControl<'bath' | 'spa'>('bath', { nonNullable: true, validators: [ Validators.required ] }),
      message: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
      acceptTerms: new FormControl(user?.authCompleted ?? false, {
        nonNullable: true,
        validators: [ Validators.requiredTrue ]
      }),
    });
    effect(() => {
      this.form.get('center')?.setValue(this.selectionService.center());
    });
  }

  get acceptTerms(): boolean {
    return this.form?.get('acceptTerms')?.value ?? false;
  }

  set acceptTerms(value: boolean) {
    this.form?.get('acceptTerms')?.setValue(value);
  }

  /**
   * Select current center
   * @param center The selected center
   */
  selectCenter(center: Center): void {
    this.selectionService.center.set(center);
  }

  submit = async (): Promise<void> => {
    const center = this.selectionService.center()!;
    const form = this.form.getRawValue();
    try {
      await this.contactService.contact(
        form.target === 'bath' ? center.contacts : center.spaContacts,
        Template.ContactEmail,
        form);
      this.notificationService.open({
        type: 'success',
        title: 'mailContacted',
        message: 'mailContactedDescription'
      });
      this.form.reset();
      this.form.get('center')?.setValue(this.selectionService.center());
      this.form.updateValueAndValidity()
    } catch (err) {
      const error = err as Error;
      this.notificationService.open({ type: 'error', title: error.details.code, message: error.message });
    }
  }
}
